import React, { createContext, useContext, useReducer, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import Analytics from '../utils/analytics';
import { toast } from 'react-toastify';

const CartContext = createContext();

// Cart operations are now managed via HTTP-only cookies on the backend
// No need for manual cookie management in frontend

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART':
      return { ...state, items: action.payload.items || [] };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const [cartId, setCartId] = useState(null); // Cart ID now managed by backend cookies
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupProduct, setPopupProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const prevAuthRef = React.useRef(isAuthenticated);

  // Handle cart based on authentication state
  useEffect(() => {
    // Only clear cart if user just logged out (was authenticated, now not)
    if (prevAuthRef.current && !isAuthenticated) {
      dispatch({ type: 'CLEAR_CART' });
      setCartId(null);
      setLoading(false);
    }
    // Always fetch user cart after login (even if cart has items)
    if (isAuthenticated && !prevAuthRef.current) {
      const fetchUserCart = async () => {
        setLoading(true);
        try {
          const cart = await api.getCartFromCookies();
          dispatch({ type: 'SET_CART', payload: cart });
          setCartId(cart._id);
        } catch (err) {
          console.error('Failed to fetch user cart:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchUserCart();
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Initialize cart from backend on app start
  useEffect(() => {
    const initializeCart = async () => {
      setLoading(true);
      try {
        let cart;
        // Always use getCartFromCookies - it handles both authenticated and guest users
        try {
          cart = await api.getCartFromCookies();
        } catch (err) {
          // No cart found, will create one when needed
        }
        
        if (cart) {
          dispatch({ type: 'SET_CART', payload: cart });
          setCartId(cart._id);
        }
      } catch (err) {
        console.error('Failed to initialize cart:', err);
      } finally {
        setLoading(false);
      }
    };

    // Only initialize if not already loaded
    if (state.items.length === 0) {
      initializeCart();
    }
  }, [isAuthenticated, state.items.length]);

  // Define clearCart with useCallback to prevent unnecessary re-renders
  const clearCart = useCallback(async () => {
    setCartId(null);
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const addToCart = async (product, quantity = 1) => {
    // Use HTTP-only cookie-based cart management
    await api.addItemToCart(product._id || product.id, quantity);
    // Fetch updated cart from backend
    try {
      const cart = await api.getCartFromCookies();
      dispatch({ type: 'SET_CART', payload: cart });
      setCartId(cart._id);
    } catch (err) {
      console.error('Failed to fetch updated cart:', err);
    }
    // Track AddToCart event
    Analytics.trackAddToCart(product, quantity);
    setPopupProduct(product);
    setPopupVisible(true);
  };

  // Re-read the cart from the backend so local state matches what the server has
  const refreshCart = async () => {
    try {
      const cart = await api.getCartFromCookies();
      dispatch({ type: 'SET_CART', payload: cart });
      setCartId(cart._id);
    } catch (err) {
      console.error('Failed to fetch updated cart:', err);
    }
  };

  // Run a cart mutation without letting a failed request escape to the UI.
  // A 404 means the cart we were editing is gone (e.g. deleted after payment); resyncing
  // picks up the current cart so the next action works.
  const mutateCart = async (mutation) => {
    try {
      await mutation();
    } catch (err) {
      console.error('Cart update failed:', err);
      toast.error('Sepetiniz güncellenemedi. Lütfen tekrar deneyin.');
    }
    await refreshCart();
  };

  const removeFromCart = (productId) =>
    mutateCart(() => api.removeItemFromCart(productId));

  const updateQuantity = (productId, quantity) =>
    mutateCart(() => api.updateCartItem(productId, quantity));

  const getCartTotal = useCallback(() => {
    return state.items.reduce((total, item) => total + (item.productId.price * item.quantity), 0);
  }, [state.items]);

  const getCartItemCount = useCallback(() => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  }, [state.items]);

  const closePopup = () => {
    setPopupVisible(false);
    setPopupProduct(null);
  };

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartItemCount,
        popupVisible,
        popupProduct,
        closePopup,
        cartId,
        dispatch,
        setCartId,
        loading, // <-- Expose loading state
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext); 